<template>
  <!-- Only present when the user has opted to see ids (Settings → Show ids). -->
  <div v-if="settings.showIds" class="st-id" :class="{ 'st-id--on-shell': onShell }">
    <span class="st-id__label">{{ label }}</span>

    <!-- Editing: the field editor plus a regenerate button (only visible here). -->
    <template v-if="editing">
      <StTextInput
        ref="inputEl"
        :model-value="modelValue"
        mono
        :on-shell="onShell"
        class="st-id__input"
        @update:model-value="emit('update:modelValue', $event)"
        @keyup.enter="stopEditing"
        @blur="stopEditing"
      />
      <StIconButton
        icon="arrows-rotate"
        label="Regenerate id"
        size="sm"
        :on-shell="onShell"
        @mousedown.prevent
        @click="regenerate"
      />
      <StIconButton
        icon="check"
        label="Done editing id"
        size="sm"
        :on-shell="onShell"
        @mousedown.prevent
        @click="stopEditing"
      />
    </template>

    <!-- Resting: a plain label-style value with a pencil to start editing. -->
    <template v-else>
      <code class="st-id__value" :title="modelValue">{{ modelValue || '—' }}</code>
      <StIconButton
        class="st-id__edit"
        icon="pencil"
        label="Edit id"
        size="sm"
        :on-shell="onShell"
        @click="startEdit"
      />
    </template>
  </div>
</template>

<script setup lang="ts">
// Inline, opt-in id editor. Hidden unless `settings.showIds` is on. At rest it reads
// as a plain label — "ID  <value>" with a pencil. Clicking the pencil expands it into
// a field editor with a circle-arrow button that mints a fresh `prefix-uuid`
// (matching BSData, whose ids are uuids).
import { nextTick, ref } from 'vue';
import { makeId } from '@scrolldex/shared';
import StTextInput from './StTextInput.vue';
import StIconButton from './StIconButton.vue';
import { useSettings } from '../../state/settings.js';

const props = withDefaults(
  defineProps<{
    modelValue: string;
    /** Prefix for a regenerated id, e.g. "ability" → "ability-<uuid>". */
    prefix?: string;
    label?: string;
    /** Theme for the dark header shell (e.g. in a TitleBar). */
    onShell?: boolean;
  }>(),
  { prefix: '', label: 'ID', onShell: false },
);

const emit = defineEmits<{ (e: 'update:modelValue', value: string): void }>();

const settings = useSettings();
const editing = ref(false);
const inputEl = ref<InstanceType<typeof StTextInput> | null>(null);

async function startEdit() {
  editing.value = true;
  await nextTick();
  inputEl.value?.$el?.focus();
}
function stopEditing() {
  editing.value = false;
}
function regenerate() {
  emit('update:modelValue', makeId(props.prefix || undefined));
}
</script>

<style scoped>
/* At rest this is just a label line — no box, like the title bar's name. */
.st-id {
  display: inline-flex;
  align-items: center;
  gap: var(--st-space-1);
  min-width: 0;
  max-width: 100%;
}
.st-id__label {
  font-size: var(--st-text-2xs);
  font-weight: var(--st-weight-semibold);
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--st-text-subtle);
  flex: none;
}
.st-id__value {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-family: var(--st-font-mono);
  font-size: var(--st-text-xs);
  color: var(--st-text-muted);
}
.st-id__input {
  flex: 1;
  min-width: 0;
}

/* ---- on the dark header shell (TitleBar) ---- */
.st-id--on-shell {
  align-self: flex-start;
}
.st-id--on-shell .st-id__label {
  color: var(--st-shell-text-subtle);
}
.st-id--on-shell .st-id__value {
  color: var(--st-shell-text-muted);
  max-width: 32rem;
}
</style>
